import React, { useState } from 'react';
import { ArrowLeft, MapPin, Calendar, Heart, CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import SwoopyLogo from '../components/SwoopyLogo';

export default function ResultsPage({ userProfile, tripRequest, results, onViewDetails, onBack }) {
  const [savedTrips, setSavedTrips] = useState([]);

  const handleSaveTrip = (tripId) => {
    if (savedTrips.includes(tripId)) {
      setSavedTrips(savedTrips.filter(id => id !== tripId));
    } else {
      setSavedTrips([...savedTrips, tripId]);
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbols = {
      'IDR': 'Rp', 'USD': '$', 'EUR': '€', 'GBP': '£',
      'SGD': 'S$', 'MYR': 'RM', 'THB': '฿', 'JPY': '¥',
      'AUD': 'A$', 'TRY': '₺'
    };
    return `${symbols[currency] || currency}${amount.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen pb-6" style={{ backgroundColor: 'var(--bg)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-3"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="max-w-lg mx-auto flex items-center">
          <button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2 -ml-2 rounded-full transition-colors"
            style={{ color: 'var(--muted)' }}
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4">
        {/* Welcome Card */}
        <div 
          className="swoopy-card p-5 mb-6"
          style={{ backgroundColor: 'var(--coral-soft)', border: 'none' }}
        >
          <div className="flex items-start gap-4">
            <SwoopyLogo variant="icon" size={68} />
            <div>
              <h1 className="text-lg font-semibold mb-1" style={{ color: 'var(--text)' }}>
                Hey {userProfile?.name}! 🎉
              </h1>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Found {results.trips.length} trips to {results.destination}
              </p>
            </div>
          </div>
        </div>

        {/* Trip Cards */}
        <div className="space-y-4">
          {results.trips.map((trip) => (
            <div 
              key={trip.id} 
              className="swoopy-card p-5"
              data-testid={`trip-card-${trip.id}`}
            >
              {/* Match Badge & Save */}
              <div className="flex items-center justify-between mb-4">
                <div className="swoopy-match">
                  <Sparkles size={14} />
                  {trip.swoopyMatch}% Match
                </div>
                <button
                  onClick={() => handleSaveTrip(trip.id)}
                  className="p-2 rounded-full transition-colors"
                  style={{ 
                    backgroundColor: savedTrips.includes(trip.id) ? 'var(--coral-soft)' : 'transparent',
                    color: savedTrips.includes(trip.id) ? 'var(--coral)' : 'var(--muted)'
                  }}
                >
                  <Heart size={20} fill={savedTrips.includes(trip.id) ? 'var(--coral)' : 'none'} />
                </button>
              </div>
              
              {/* Trip Info */}
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text)' }}>
                {trip.title}
              </h3>
              <div className="flex items-center gap-2 text-sm mb-3" style={{ color: 'var(--muted)' }}>
                <MapPin size={14} />
                <span>{trip.continent} • {trip.country}</span>
              </div>

              {/* Cities */}
              <div className="flex flex-wrap gap-2 mb-3">
                {trip.cities.map((city, index) => (
                  <span 
                    key={index} 
                    className="text-xs px-3 py-1 rounded-full font-medium"
                    style={{ backgroundColor: 'var(--border-color)', color: 'var(--text)' }}
                  >
                    {city}
                  </span>
                ))}
              </div>

              {/* Duration */}
              <div className="flex items-center gap-2 text-sm mb-4" style={{ color: 'var(--muted)' }}>
                <Calendar size={14} />
                <span>{trip.duration} days</span>
              </div>

              {/* Budget */}
              <div 
                className="p-4 rounded-2xl mb-4"
                style={{ backgroundColor: 'var(--bg)' }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>Total</p>
                    <p className="font-semibold" style={{ color: 'var(--text)' }}>
                      {formatCurrency(trip.totalBudgetMin, trip.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>Daily/Person</p>
                    <p className="font-semibold" style={{ color: 'var(--text)' }}>
                      {formatCurrency(trip.dailyBudgetMin, trip.currency)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Why Match */}
              <div className="mb-4">
                <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>Why you'll love it:</p>
                <ul className="space-y-1.5">
                  {trip.whyMatches.slice(0, 2).map((reason, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm" style={{ color: 'var(--muted)' }}>
                      <CheckCircle size={16} style={{ color: 'var(--coral)', flexShrink: 0, marginTop: '2px' }} />
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {trip.tags.slice(0, 3).map((tag, index) => (
                  <span key={index} className="swoopy-tag text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => onViewDetails(trip)}
                className="swoopy-btn w-full gap-2"
                data-testid={`swoop-trip-btn-${trip.id}`}
              >
                <span>Swoop this trip</span>
                <ArrowRight size={18} />
              </button>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <p 
          className="text-center text-xs mt-6 px-4"
          style={{ color: 'var(--muted)' }}
        >
          Estimates are approximate — use them as your starting point! 📍
        </p>
      </div>
    </div>
  );
}
